import React, { Fragment, useEffect, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { Button } from '../UI/Button';
import { useToast } from '../UI/ToastProvider';
import { useHelpdeskHomePath } from '../../hooks/useHelpdeskHomePath';
import {
  HELPDESK_HOME_SCREEN_OPTIONS,
  HelpdeskHomeScreen,
} from '../../utils/helpdeskHomeScreen';

interface HelpdeskPreferencesModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const HelpdeskPreferencesModal: React.FC<HelpdeskPreferencesModalProps> = ({
  isOpen,
  onClose,
}) => {
  const { addToast } = useToast();
  const {
    personalScreen,
    adminScreen,
    setPersonalPreference,
  } = useHelpdeskHomePath();
  const [selected, setSelected] = useState<HelpdeskHomeScreen | 'org-default'>(
    personalScreen ?? 'org-default',
  );

  useEffect(() => {
    if (!isOpen)
    {
      return;
    }

    setSelected(personalScreen ?? 'org-default');
  }, [isOpen, personalScreen]);

  const handleSave = () => {
    if (selected === 'org-default')
    {
      setPersonalPreference(null);
    }
    else
    {
      setPersonalPreference(selected);
    }

    addToast('Preferência de tela inicial salva', 'success');
    onClose();
  };

  const adminLabel = HELPDESK_HOME_SCREEN_OPTIONS.find(option => option.value === adminScreen)?.label
    ?? 'Console';

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/40" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-lg bg-white shadow-xl transition-all">
                <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
                  <div>
                    <Dialog.Title className="text-lg font-semibold text-gray-900">
                      Preferências do atendimento
                    </Dialog.Title>
                    <p className="text-sm text-gray-500 mt-0.5">
                      Define a tela aberta ao entrar no módulo.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={onClose}
                    className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                    aria-label="Fechar"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>

                <div className="px-5 py-4 space-y-3">
                  <label
                    className={`flex items-start gap-3 cursor-pointer rounded-md border-2 p-3 transition-all ${
                      selected === 'org-default'
                        ? 'border-blue-600 bg-blue-50'
                        : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <input
                      type="radio"
                      name="helpdesk-home"
                      className="sr-only"
                      checked={selected === 'org-default'}
                      onChange={() => setSelected('org-default')}
                    />
                    <span>
                      <span className="block font-medium text-gray-900">Usar padrão da organização</span>
                      <span className="block text-sm text-gray-500">
                        Atualmente: {adminLabel}
                      </span>
                    </span>
                  </label>

                  {HELPDESK_HOME_SCREEN_OPTIONS.map(option => (
                    <label
                      key={option.value}
                      className={`flex items-start gap-3 cursor-pointer rounded-md border-2 p-3 transition-all ${
                        selected === option.value
                          ? 'border-blue-600 bg-blue-50'
                          : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      <input
                        type="radio"
                        name="helpdesk-home"
                        className="sr-only"
                        checked={selected === option.value}
                        onChange={() => setSelected(option.value)}
                      />
                      <svg
                        className={`h-5 w-5 mt-0.5 flex-shrink-0 ${
                          selected === option.value ? 'text-blue-600' : 'text-gray-400'
                        }`}
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={option.icon} />
                      </svg>
                      <span>
                        <span className="block font-medium text-gray-900">{option.label}</span>
                        <span className="block text-sm text-gray-500">{option.description}</span>
                      </span>
                    </label>
                  ))}
                </div>

                <div className="flex justify-end gap-2 border-t border-gray-200 px-5 py-4">
                  <Button variant="secondary" onClick={onClose}>
                    Cancelar
                  </Button>
                  <Button variant="primary" onClick={handleSave}>
                    Salvar
                  </Button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};
